import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Scatter } from 'react-chartjs-2';
import 'chart.js/auto';
import astroApi from '../../api/astro';
import metadataApi from '../../api/metadata';
import { parsePlotInstructions, buildDefaultPlot } from '../../utils/plotInstructions';

const MINI_PALETTE = [
  'rgba(99,102,241,0.85)',
  'rgba(239,68,68,0.85)',
  'rgba(34,197,94,0.85)',
  'rgba(168,85,247,0.85)',
  'rgba(234,179,8,0.85)',
  'rgba(20,184,166,0.85)',
  'rgba(251,146,60,0.85)',
];

const MiniPlot = ({ config }) => {
  const { xLabel, yLabel, data, options = {}, isMultiSeries, seriesData } = config;

  let datasets;
  if (isMultiSeries && seriesData) {
    datasets = seriesData.map((series, i) => {
      const color = series.color || MINI_PALETTE[i % MINI_PALETTE.length];
      const isLarge = series.data.length > 20;
      const showLine = options.connectPoints || isLarge;
      return {
        label: series.label,
        data: series.data,
        backgroundColor: color,
        borderColor: color,
        pointRadius: showLine ? (isLarge ? 0 : 2) : 2,
        pointHoverRadius: showLine ? (isLarge ? 2 : 3) : 3,
        showLine,
        borderWidth: 1,
        fill: false,
      };
    });
  } else {
    const pts = data || [];
    const isLarge = pts.length > 100;
    const showLine = isLarge || options.connectPoints || false;
    datasets = [{
      data: pts,
      backgroundColor: 'rgba(99,102,241,0.75)',
      borderColor: 'rgba(99,102,241,1)',
      pointRadius: showLine ? (isLarge ? 0 : 2) : 3,
      pointHoverRadius: showLine ? (isLarge ? 2 : 3) : 4,
      showLine,
      borderWidth: 1.5,
      fill: false,
    }];
  }

  const chartData = { datasets };
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    plugins: {
      legend: {
        display: isMultiSeries && seriesData && seriesData.length > 1,
        position: 'bottom',
        labels: { boxWidth: 8, font: { size: 8 }, padding: 4 },
      },
      title: { display: false },
      tooltip: { callbacks: { label: (ctx) => `(${Number(ctx.raw.x).toFixed(2)}, ${Number(ctx.raw.y).toFixed(3)})` } }
    },
    scales: {
      x: {
        title: { display: true, text: xLabel, font: { size: 9 }, color: '#888' },
        ticks: { font: { size: 8 }, maxTicksLimit: 5 },
        grid: { color: 'rgba(128,128,128,0.15)' }
      },
      y: {
        reverse: options.invertYAxis === true,
        title: { display: true, text: yLabel, font: { size: 9 }, color: '#888' },
        ticks: { font: { size: 8 }, maxTicksLimit: 4 },
        grid: { color: 'rgba(128,128,128,0.15)' }
      }
    }
  };
  return (
    <div style={{ width: '340px', height: '160px' }}>
      <Scatter data={chartData} options={chartOptions} />
    </div>
  );
};

const ConeSearchContent = ({ showTitle = true }) => {
  const [loading, setLoading] = useState(false);
  const [tables, setTables] = useState([]);
  const [selectedTable, setSelectedTable] = useState('');
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [ra, setRa] = useState('');
  const [dec, setDec] = useState('');
  const [radius, setRadius] = useState('0.00023');
  const [limit, setLimit] = useState('10');
  const [selectedImportantColumns, setSelectedImportantColumns] = useState([]);
  const [showAllColumns, setShowAllColumns] = useState(false);
  const [availableColumns, setAvailableColumns] = useState([]);
  const [loadingColumns, setLoadingColumns] = useState(false);
  const [columnFilter, setColumnFilter] = useState('');
  const [resultFieldFilter, setResultFieldFilter] = useState('');
  const [expandedRows, setExpandedRows] = useState(new Set());
  const navigate = useNavigate();

  useEffect(() => {
    const fetchTables = async () => {
      try {
        setLoading(true);
        const astroTables = await astroApi.getAstroTables();
        setTables(astroTables);
        if (astroTables.length > 0) {
          const firstTable = `${astroTables[0].schema_name}.${astroTables[0].table_name}`;
          setSelectedTable(firstTable);
          setSelectedImportantColumns(astroTables[0].important_columns || []);
          setShowAllColumns(false);
          await loadColumnsForTable(astroTables[0].schema_name, astroTables[0].table_name);
        }
      } catch (err) {
        console.error('Failed to fetch astro tables:', err);
        setError('Failed to load astronomical tables. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    fetchTables();
  }, []);

  const getSelectedTableMetadata = () => {
    if (!selectedTable) return null;
    return tables.find(table => `${table.schema_name}.${table.table_name}` === selectedTable);
  };

  const loadColumnsForTable = async (schemaName, tableName) => {
    try {
      setLoadingColumns(true);
      const columns = await metadataApi.getColumns(schemaName, tableName);
      setAvailableColumns(columns);
    } catch (err) {
      console.error('Failed to load columns:', err);
      setError('Failed to load columns for the selected table.');
    } finally {
      setLoadingColumns(false);
    }
  };

  const handleTableSelection = async (tableKey) => {
    setSelectedTable(tableKey);
    const tableMetadata = tables.find(table => `${table.schema_name}.${table.table_name}` === tableKey);
    setSelectedImportantColumns(tableMetadata?.important_columns || []);
    setShowAllColumns(false);
    setResults(null);
    setColumnFilter('');
    if (tableMetadata) {
      await loadColumnsForTable(tableMetadata.schema_name, tableMetadata.table_name);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedTable || !ra || !dec || !radius) {
      setError('Please fill in all required fields');
      return;
    }
    try {
      setLoading(true);
      setError(null);
      setResults(null);
      setExpandedRows(new Set());
      setResultFieldFilter('');
      const columnsToRequest = showAllColumns ? null : selectedImportantColumns;
      const response = await astroApi.performConeSearch(
        selectedTable,
        parseFloat(ra),
        parseFloat(dec),
        parseFloat(radius),
        parseInt(limit),
        columnsToRequest
      );
      setResults(response);
    } catch (err) {
      console.error('Error performing cone search:', err);
      setError(err.response?.data?.detail || 'An error occurred during the cone search');
    } finally {
      setLoading(false);
    }
  };

  // Build mini-plot configs per result row whenever results change
  const plotConfigs = useMemo(() => {
    if (!results?.metadata?.plotting_instructions) return {};
    const instructions = parsePlotInstructions(results.metadata.plotting_instructions);
    if (!instructions) return {};
    const map = {};
    results.results.forEach((obj, idx) => {
      for (const plotDef of instructions.plots) {
        const config = buildDefaultPlot({ defaultPlot: plotDef }, obj);
        if (config) { map[idx] = config; break; }
      }
    });
    return map;
  }, [results]);

  const handleObjectClick = (object) => {
    const idColumn = results?.metadata?.id_column || Object.keys(object)[0];
    const idValue = object[idColumn];
    navigate(`/object/${selectedTable}/${idColumn}/${idValue}`, {
      state: { objectData: object, metadata: results.metadata }
    });
  };

  if (loading && !results && tables.length === 0) {
    return (
      <div>
        {showTitle && <h1 className="text-3xl font-bold text-ui-light-text-primary dark:text-ui-dark-text-primary mb-6">Cone Search</h1>}
        <div className="flex justify-center">
          <svg className="animate-spin h-10 w-10 text-primary-600 dark:text-primary-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </div>
        <p className="text-center text-ui-light-text-secondary dark:text-ui-dark-text-secondary mt-2">Loading...</p>
      </div>
    );
  }

  return (
    <div>
      {showTitle && <h1 className="text-3xl font-bold text-ui-light-text-primary dark:text-ui-dark-text-primary mb-6">Cone Search</h1>}

      {error && (
        <div className="bg-red-100 dark:bg-red-900/30 border border-red-400 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded mb-4">
          <strong className="font-bold">Error: </strong>
          <span>{error}</span>
        </div>
      )}

      <div className="bg-ui-light-card dark:bg-ui-dark-card shadow-md rounded-lg p-5 mb-6 border border-ui-light-border dark:border-ui-dark-border">
        <form onSubmit={handleSubmit}>

          {/* Coordinates row */}
          <div className="flex flex-wrap items-end gap-2 mb-5">
            <div className="flex-1 min-w-28">
              <label className="block text-xs font-medium text-ui-light-text-secondary dark:text-ui-dark-text-secondary mb-1">
                RA <span className="font-normal opacity-60">deg</span>
              </label>
              <input type="number" step="any" value={ra} onChange={(e) => setRa(e.target.value)}
                className="w-full px-3 py-1.5 text-sm border border-ui-light-border dark:border-ui-dark-border rounded-md bg-ui-light-background dark:bg-ui-dark-background text-ui-light-text-primary dark:text-ui-dark-text-primary focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors"
                placeholder="150.123" disabled={loading} required />
            </div>
            <div className="flex-1 min-w-28">
              <label className="block text-xs font-medium text-ui-light-text-secondary dark:text-ui-dark-text-secondary mb-1">
                Dec <span className="font-normal opacity-60">deg</span>
              </label>
              <input type="number" step="any" value={dec} onChange={(e) => setDec(e.target.value)}
                className="w-full px-3 py-1.5 text-sm border border-ui-light-border dark:border-ui-dark-border rounded-md bg-ui-light-background dark:bg-ui-dark-background text-ui-light-text-primary dark:text-ui-dark-text-primary focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors"
                placeholder="−30.456" disabled={loading} required />
            </div>
            <div className="w-32">
              <label className="block text-xs font-medium text-ui-light-text-secondary dark:text-ui-dark-text-secondary mb-1">
                Radius <span className="font-normal opacity-60">deg · max 1</span>
              </label>
              <input type="number" step="any" max="1" value={radius} onChange={(e) => setRadius(e.target.value)}
                className="w-full px-3 py-1.5 text-sm border border-ui-light-border dark:border-ui-dark-border rounded-md bg-ui-light-background dark:bg-ui-dark-background text-ui-light-text-primary dark:text-ui-dark-text-primary focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors"
                disabled={loading} required />
            </div>
            <div className="w-24">
              <label className="block text-xs font-medium text-ui-light-text-secondary dark:text-ui-dark-text-secondary mb-1">
                Limit <span className="font-normal opacity-60">max 100</span>
              </label>
              <input type="number" min="1" max="100" value={limit} onChange={(e) => setLimit(e.target.value)}
                className="w-full px-3 py-1.5 text-sm border border-ui-light-border dark:border-ui-dark-border rounded-md bg-ui-light-background dark:bg-ui-dark-background text-ui-light-text-primary dark:text-ui-dark-text-primary focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors"
                disabled={loading} />
            </div>
            <button type="submit"
              className="px-5 py-1.5 text-sm font-medium text-white bg-primary-600 dark:bg-primary-500 rounded-md hover:bg-primary-700 dark:hover:bg-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors disabled:opacity-50 flex items-center gap-2 self-end"
              disabled={loading || !selectedTable}>
              {loading ? (
                <>
                  <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Searching…
                </>
              ) : 'Search'}
            </button>
          </div>

          {/* Table Selection */}
          <div className="mb-5">
            <p className="text-xs font-medium text-ui-light-text-secondary dark:text-ui-dark-text-secondary mb-2">Table</p>
            {tables.length === 0 ? (
              <p className="text-sm text-ui-light-text-secondary dark:text-ui-dark-text-secondary">
                No astronomical tables available. Please contact an administrator to add tables.
              </p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {tables.map((table) => {
                  const tableKey = `${table.schema_name}.${table.table_name}`;
                  const isSelected = selectedTable === tableKey;
                  return (
                    <button
                      key={tableKey}
                      type="button"
                      onClick={() => handleTableSelection(tableKey)}
                      title={tableKey}
                      className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-150 ${
                        isSelected
                          ? 'bg-primary-600 dark:bg-primary-500 text-white shadow-sm'
                          : 'bg-ui-light-background dark:bg-ui-dark-background border border-ui-light-border dark:border-ui-dark-border text-ui-light-text-secondary dark:text-ui-dark-text-secondary hover:border-primary-400 dark:hover:border-primary-500 hover:text-primary-600 dark:hover:text-primary-400'
                      }`}
                    >
                      {table.display_name || tableKey}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Column Selection */}
          {selectedTable && (
            <div className="border-t border-ui-light-border dark:border-ui-dark-border pt-4">
              <div className="flex items-center gap-4 mb-3">
                <p className="text-xs font-medium text-ui-light-text-secondary dark:text-ui-dark-text-secondary">Columns</p>
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <input type="radio" name="column-selection" checked={!showAllColumns}
                      onChange={() => setShowAllColumns(false)}
                      className="text-primary-600 dark:text-primary-400 focus:ring-primary-500" />
                    <span className="text-xs text-ui-light-text-primary dark:text-ui-dark-text-primary">Custom</span>
                  </label>
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <input type="radio" name="column-selection" checked={showAllColumns}
                      onChange={() => setShowAllColumns(true)}
                      className="text-primary-600 dark:text-primary-400 focus:ring-primary-500" />
                    <span className="text-xs text-ui-light-text-primary dark:text-ui-dark-text-primary">All (*)</span>
                  </label>
                </div>
              </div>

              {!showAllColumns && (
                <div className="space-y-2">
                  <input type="text" value={columnFilter} onChange={(e) => setColumnFilter(e.target.value)}
                    placeholder="Filter columns…"
                    className="w-full px-3 py-1.5 text-sm border border-ui-light-border dark:border-ui-dark-border rounded-md bg-ui-light-card dark:bg-ui-dark-card text-ui-light-text-primary dark:text-ui-dark-text-primary focus:outline-none focus:ring-2 focus:ring-primary-500" />
                  {loadingColumns ? (
                    <div className="flex items-center gap-2 py-3">
                      <svg className="animate-spin h-4 w-4 text-primary-600 dark:text-primary-400" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span className="text-xs text-ui-light-text-secondary dark:text-ui-dark-text-secondary">Loading columns…</span>
                    </div>
                  ) : (
                    <div className="max-h-36 overflow-y-auto border border-ui-light-border dark:border-ui-dark-border rounded-md p-2">
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-0.5">
                        {availableColumns
                          .filter(col => !columnFilter || col.name.toLowerCase().includes(columnFilter.toLowerCase()))
                          .map(col => (
                            <label key={col.name} className="flex items-center gap-2 px-2 py-1 rounded hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover cursor-pointer">
                              <input type="checkbox" checked={selectedImportantColumns.includes(col.name)}
                                onChange={(e) => {
                                  if (e.target.checked) setSelectedImportantColumns(prev => [...prev, col.name]);
                                  else setSelectedImportantColumns(prev => prev.filter(c => c !== col.name));
                                }}
                                className="rounded text-primary-600 dark:text-primary-400 focus:ring-primary-500 flex-shrink-0" />
                              <span className="text-xs text-ui-light-text-primary dark:text-ui-dark-text-primary truncate" title={col.name}>{col.name}</span>
                            </label>
                          ))}
                      </div>
                    </div>
                  )}
                  {selectedImportantColumns.length > 0 && (
                    <div className="flex flex-wrap items-center gap-1 pt-1">
                      {selectedImportantColumns.map(col => (
                        <span key={col} className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded-full">
                          {col}
                          <button type="button" onClick={() => setSelectedImportantColumns(prev => prev.filter(c => c !== col))}
                            className="text-primary-500 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-200 leading-none">×</button>
                        </span>
                      ))}
                      <button type="button" onClick={() => setSelectedImportantColumns([])}
                        className="text-xs text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary hover:text-red-500 dark:hover:text-red-400 transition-colors ml-1">
                        clear all
                      </button>
                    </div>
                  )}
                </div>
              )}

              {showAllColumns && (
                <p className="text-xs text-ui-light-text-secondary dark:text-ui-dark-text-secondary italic">
                  All available columns will be returned in the results.
                </p>
              )}
            </div>
          )}

        </form>
      </div>

      {results && (
        <div className="bg-ui-light-card dark:bg-ui-dark-card shadow-md rounded-lg p-5 border border-ui-light-border dark:border-ui-dark-border">
          {/* Results header */}
          <div className="flex items-center justify-between gap-3 mb-4">
            <h2 className="text-sm font-semibold text-ui-light-text-secondary dark:text-ui-dark-text-secondary uppercase tracking-wider flex-shrink-0">
              {results.results.length} object{results.results.length !== 1 ? 's' : ''} found
            </h2>
            <input
              type="text"
              placeholder="Filter fields…"
              value={resultFieldFilter}
              onChange={e => setResultFieldFilter(e.target.value)}
              className="w-44 px-3 py-1 text-xs border border-ui-light-border dark:border-ui-dark-border rounded-md bg-ui-light-background dark:bg-ui-dark-background text-ui-light-text-primary dark:text-ui-dark-text-primary focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          {results.results.length === 0 ? (
            <p className="py-4 text-center text-sm text-ui-light-text-secondary dark:text-ui-dark-text-secondary">
              No objects found within the specified search radius.
            </p>
          ) : (
            <div className="space-y-3">
              {results.results.map((object, index) => {
                const plotConfig = plotConfigs[index];
                const importantCols = results.metadata.important_columns || [];
                const isExpanded = expandedRows.has(index);

                const allDataEntries = Object.entries(object).filter(([k]) => k !== 'image_url');
                const searchFiltered = resultFieldFilter
                  ? allDataEntries.filter(([k]) => k.toLowerCase().includes(resultFieldFilter.toLowerCase()))
                  : allDataEntries;

                // Default: show important columns only; fallback to first 7 if none defined
                const defaultEntries = importantCols.length > 0
                  ? searchFiltered.filter(([k]) => importantCols.includes(k))
                  : searchFiltered.slice(0, 7);
                const visibleEntries = isExpanded || resultFieldFilter ? searchFiltered : defaultEntries;
                const hiddenCount = searchFiltered.length - defaultEntries.length;

                return (
                  <div key={index} className="rounded-lg border border-ui-light-border dark:border-ui-dark-border bg-ui-light-background dark:bg-ui-dark-background overflow-hidden">
                    {/* Visual row: link icon + image + plot */}
                    <div className="flex items-start gap-3 p-3">
                      <button
                        onClick={() => handleObjectClick(object)}
                        className="flex-shrink-0 mt-1 p-1.5 rounded-md text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary hover:text-primary-600 dark:hover:text-primary-400 hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover transition-colors"
                        title="View details"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                      </button>

                      <div className="flex-shrink-0">
                        {object.image_url ? (
                          <img
                            src={object.image_url}
                            alt="Object thumbnail"
                            className="h-32 w-32 rounded-md object-cover cursor-pointer hover:opacity-90 transition-opacity"
                            onClick={() => handleObjectClick(object)}
                          />
                        ) : (
                          <div className="h-16 w-16 rounded-md bg-ui-light-card dark:bg-ui-dark-card border border-ui-light-border dark:border-ui-dark-border flex items-center justify-center">
                            <span className="text-xs text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary">—</span>
                          </div>
                        )}
                      </div>

                      {plotConfig && (
                        <div className="flex-shrink-0">
                          <MiniPlot config={plotConfig} />
                        </div>
                      )}
                    </div>

                    {/* Data fields */}
                    <div className="px-3 pb-2 border-t border-ui-light-border dark:border-ui-dark-border pt-2">
                      <div className="flex flex-wrap gap-x-5 gap-y-0.5">
                        {visibleEntries.map(([key, value]) => {
                          const isRaDec = key === results.metadata.ra_column || key === results.metadata.dec_column;
                          const display = value !== null
                            ? (isRaDec && typeof value === 'number' ? value.toFixed(6) : String(value))
                            : '—';
                          const isImportant = importantCols.includes(key);
                          return (
                            <div key={key} className="flex items-baseline gap-1 min-w-0">
                              <span className={`text-xs whitespace-nowrap ${isImportant ? 'text-primary-500 dark:text-primary-400 font-medium' : 'text-ui-light-text-tertiary dark:text-ui-dark-text-tertiary'}`}>
                                {key}:
                              </span>
                              <span className="text-xs text-ui-light-text-primary dark:text-ui-dark-text-primary truncate max-w-36" title={display}>
                                {display}
                              </span>
                            </div>
                          );
                        })}
                      </div>

                      {/* Expand / collapse toggle */}
                      {!resultFieldFilter && hiddenCount > 0 && (
                        <button
                          onClick={() => setExpandedRows(prev => {
                            const next = new Set(prev);
                            if (isExpanded) next.delete(index); else next.add(index);
                            return next;
                          })}
                          className="mt-1.5 text-xs text-primary-500 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
                        >
                          {isExpanded ? '↑ show less' : `+${hiddenCount} more fields`}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ConeSearchContent;
